import type { PublicAssetShare } from 'fym-shared/src/music24/assetShares';

const FACT_SEPARATOR = ' · ';

/** A video at or under this length loops (a Canvas loop, a motion artwork, a short loop). */
export const LOOP_MAX_SECONDS = 10;

/** At or under this width-to-height ratio a file is tall (9:16 = 0.5625, the motion artwork tall 0.75). */
const TALL_MAX_RATIO = 0.76;

/** At or above this ratio a file is wide (16:9, 1.91:1, 3:1): it goes above the text. */
const WIDE_MIN_RATIO = 1.2;

/** `0:15`, `3:54`, `1:02:03`. */
export function formatShareDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = String(total % 60).padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${rest}`;
  }
  return `${minutes}:${rest}`;
}

/** `812 KB`, `4.1 MB`, `1.2 GB` (powers of 1000, one decimal under 10). */
export function formatShareSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  if (unit === 0) {
    return `${Math.round(value)} B`;
  }
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded} ${units[unit]}`;
}

const TYPE_LABELS: Record<string, string> = {
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/webm': 'WEBM',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
};

/** `MP4`, `JPG`; else the subtype of the content type in capitals. */
export function formatShareType(contentType: string): string {
  const base = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  const known = TYPE_LABELS[base];
  if (known !== undefined) {
    return known;
  }
  const subtype = base.split('/')[1] ?? '';
  return subtype.length > 0 ? subtype.toUpperCase() : '';
}

/**
 * The facts line of the page: "0:15 · 1080 × 1920 · MP4 · 4.1 MB". A fact that the API does not
 * give is left out. An image has no length.
 */
export function assetShareFacts(
  share: Pick<PublicAssetShare, 'mediaKind' | 'durationSec' | 'width' | 'height' | 'contentType' | 'sizeBytes'>
): string {
  const facts: string[] = [];
  if (share.mediaKind === 'video' && share.durationSec !== null && share.durationSec > 0) {
    facts.push(formatShareDuration(share.durationSec));
  }
  if (share.width !== null && share.height !== null && share.width > 0 && share.height > 0) {
    facts.push(`${share.width} × ${share.height}`);
  }
  const type = formatShareType(share.contentType);
  if (type.length > 0) {
    facts.push(type);
  }
  if (share.sizeBytes !== null && share.sizeBytes > 0) {
    facts.push(formatShareSize(share.sizeBytes));
  }
  return facts.join(FACT_SEPARATOR);
}

/** A short video loops, muted, as it does in the app it is made for (a Canvas loop, a motion artwork). */
export function shouldLoopShareVideo(share: Pick<PublicAssetShare, 'mediaKind' | 'durationSec'>): boolean {
  return share.mediaKind === 'video' && share.durationSec !== null && share.durationSec <= LOOP_MAX_SECONDS;
}

/** True for a 9:16-like file (a story, a reel): the page shows it in a narrow, phone-like frame. */
export function isTallShare(share: Pick<PublicAssetShare, 'width' | 'height'>): boolean {
  if (share.width === null || share.height === null || share.width <= 0 || share.height <= 0) {
    return true;
  }
  return share.width / share.height <= TALL_MAX_RATIO;
}

/**
 * A tall video (a reel, a story) plays full screen like a Story, with the title and the actions on
 * top (design asset-share c6). Every other file plays in the theater layout.
 */
export function isStoryShare(share: Pick<PublicAssetShare, 'mediaKind' | 'width' | 'height'>): boolean {
  return share.mediaKind === 'video' && isTallShare(share);
}

/**
 * The fact in the subtitle of the page (design asset-share c1, c5): the length of a video, the size
 * in pixels of a picture. Empty when the API does not give it.
 */
export function assetShareSubtitleFact(
  share: Pick<PublicAssetShare, 'mediaKind' | 'durationSec' | 'width' | 'height'>
): string {
  if (share.mediaKind === 'video') {
    return share.durationSec !== null && share.durationSec > 0 ? formatShareDuration(share.durationSec) : '';
  }
  if (share.width !== null && share.height !== null && share.width > 0 && share.height > 0) {
    return `${share.width} × ${share.height}`;
  }
  return '';
}

/** True for a wide file (a full video, a link preview, a banner): the page puts it above the text. */
export function isWideShare(share: Pick<PublicAssetShare, 'width' | 'height'>): boolean {
  if (share.width === null || share.height === null || share.width <= 0 || share.height <= 0) {
    return false;
  }
  return share.width / share.height >= WIDE_MIN_RATIO;
}

/** The `kind` of the events of a public page. The label of a reel is "AI reel 30s" in every language. */
export function publicShareKindOf(assetLabel: string): 'kitAsset' | 'reel' {
  return assetLabel.startsWith('AI reel') ? 'reel' : 'kitAsset';
}
